import React, { useState, useRef } from 'react';
import { ArrowRight, Sparkles, Users, GraduationCap, Calendar, Mail, Phone, CheckCircle2, ShieldCheck, Wrench, Briefcase, PlayCircle, Home, Shirt, Droplets, HeartHandshake } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
// Brand colors specific to ProMaison
const NAVY = '#1E3A5F';
const NAVY_DARK = '#152C47';
const GOLD = '#D4A574';
const GOLD_LIGHT = '#F5EBDD';
type Audience = 'personnel' | 'employeur' | null;
export function PromaisonPage() {
  const [audience, setAudience] = useState<Audience>(null);
  const parcoursRef = useRef<HTMLDivElement>(null);
  const selectAudience = (next: Audience) => {
    setAudience(next);
    // Smooth scroll to the parcours section
    setTimeout(() => {
      parcoursRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 50);
  };
  return <Layout data-id="element-1748" data-mp="pij3mnkc3p9q">
      <SEO title="PROMAISON par ADCC | Renforcement des capacités des personnels de maison" description="PROMAISON est le programme ADCC de renforcement des capacités des personnels de maison au Bénin. Deux parcours — Fondamental et Expert — pour répondre aux besoins exprimés par les employeurs. Prochaine session en octobre 2026 à Cotonou." url="https://adcc.africa/promaison" data-id="element-1749" data-mp="phzv1xgkqd8d" />

      {/* ─────────────────────────────────────────────── */}
      {/* HERO */}
      {/* ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden" style={{
      backgroundColor: NAVY
    }} data-id="element-1750" data-mp="1tzvc81abchn">
        {/* Subtle pattern */}
        <div className="absolute inset-0 opacity-10" data-id="element-1751" data-mp="1w6g3xfhjz2po">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, #D4A574 1px, transparent 1px), radial-gradient(circle at 80% 70%, #ffffff 1px, transparent 1px)',
          backgroundSize: '60px 60px, 80px 80px'
        }} data-id="element-1752" data-mp="1w3typmi4wzam" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32" data-id="element-1753" data-mp="3qfv4eeg74ppd">
          <div className="text-center max-w-4xl mx-auto" data-id="element-1754" data-mp="3bqp74w0x59k4">
            {/* PROMAISON logo on a soft round platform that absorbs its white edges */}
            <div className="relative flex justify-center mb-10" data-id="element-1755" data-mp="87njez3ejtig">
              <div className="relative w-80 h-80 md:w-[28rem] md:h-[28rem] lg:w-[32rem] lg:h-[32rem] rounded-full flex items-center justify-center" style={{
              background: 'radial-gradient(circle at 50% 50%, #ffffff 0%, #ffffff 55%, rgba(255,255,255,0.6) 70%, rgba(255,255,255,0) 85%)'
            }} data-id="element-1756" data-mp="862mj2tm7yhf">
                <img src="https://cdn.magicpatterns.com/uploads/xq7xorXDBCdoHQja5rTP8f/PROMAISON_logo_extrait.png" alt="PROMAISON par ADCC" className="w-64 md:w-80 lg:w-[24rem] h-auto object-contain" data-id="element-1757" data-mp="3ax5oif2la07" />
              </div>
            </div>

            <p className="text-xs md:text-sm font-medium tracking-[0.3em] uppercase mb-10" style={{
            color: GOLD
          }} data-id="element-1758" data-mp="2gezeiowxvcu6">
              Association Dignité Compétence Création
            </p>

            <p className="text-3xl md:text-5xl font-light italic text-white mb-6 leading-tight" style={{
            fontFamily: "'Playfair Display', Georgia, serif"
          }} data-id="element-1759" data-mp="2qcx3jemmhdnu">
              Votre maison mérite mieux.
            </p>
            <p className="text-base md:text-lg text-blue-100 max-w-2xl mx-auto leading-relaxed mb-12" data-id="element-1760" data-mp="1xn6moxtlvxo1">
              Programme de renforcement des capacités des personnels de maison
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1761" data-mp="27h6y1hfhdz57">
              <Link to="/inscription" className="inline-flex items-center justify-center gap-2 text-base px-8 py-4 rounded-md font-medium shadow-xl hover:shadow-2xl transition-all bg-white hover:bg-gray-50 cursor-pointer" style={{
              color: NAVY
            }} data-id="element-1762" data-mp="22mszj65ivbwb">
                <GraduationCap className="w-5 h-5" data-id="element-1763" data-mp="d94fxqbki9zk" />
                S'inscrire au parcours
                <ArrowRight className="w-5 h-5" data-id="element-1764" data-mp="h9ewfwwzkiso" />
              </Link>
              <button type="button" onClick={() => selectAudience('employeur')} className="inline-flex items-center justify-center gap-2 text-base px-8 py-4 rounded-md font-medium border-2 border-white text-white hover:bg-white hover:text-[#1E3A5F] transition-colors cursor-pointer" data-id="element-1765" data-mp="h7kaqgdw4mnj">
                <Briefcase className="w-5 h-5" data-id="element-1766" data-mp="vyjd39o4ueyo" />
                Je suis employeur
                <ArrowRight className="w-5 h-5" data-id="element-1767" data-mp="10wckg6169f0j" />
              </button>
            </div>

            {/* Quick video access — visible right away */}
            <div className="mt-8" data-id="element-1768" data-mp="19yc4763kmx76">
              <a href="#video" className="inline-flex items-center gap-2 text-sm font-medium text-white/90 hover:text-white transition-colors" data-id="element-1769" data-mp="19xj1qhbnatuu">
                <PlayCircle className="w-5 h-5" style={{
                color: GOLD
              }} data-id="element-1770" data-mp="36t8kkzufgopm" />
                <span className="underline underline-offset-4" data-id="element-1771" data-mp="3brs6x1cxo2ii">
                  Regarder la vidéo de présentation
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────── */}
      {/* ENQUÊTE — chiffres clés */}
      {/* ─────────────────────────────────────────────── */}
      <section className="py-20 md:py-24 bg-white" data-id="element-1772" data-mp="2pjd09pp5gno7">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1773" data-mp="3n5tu2kyocj57">
          <div className="mb-12" data-id="element-1774" data-mp="3n2xxp12h62iv">
            <span className="text-sm font-semibold tracking-widest uppercase mb-4 block" style={{
            color: GOLD
          }} data-id="element-1775" data-mp="3i5pfx1l8o0i5">
              Pourquoi PROMAISON ?
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-1776" data-mp="395ssf3al57oo">
              Un programme né des besoins exprimés par les employeurs eux-mêmes.
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-1777" data-mp="1xqdqdl3irpmp">
              En février 2026, ADCC a réalisé une enquête auprès d'employeurs de
              personnels de maison à Cotonou. Les résultats ont mis en évidence
              des préoccupations récurrentes :
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16" data-id="element-1778" data-mp="1p9lneuc0cll7">
            {[{
            pct: '62,5%',
            desc: 'des employeurs évoquent des difficultés liées à la fiabilité et à la confiance'
          }, {
            pct: '56,3%',
            desc: 'signalent une méconnaissance des outils, produits et bonnes pratiques'
          }, {
            pct: '25%',
            desc: 'rencontrent des problèmes de ponctualité ou font face à un turn-over fréquent'
          }].map((stat, idx) => <div key={idx} className="rounded-xl p-7 border-l-4 transition-shadow hover:shadow-md" style={{
            backgroundColor: '#F1F5F9',
            borderColor: NAVY
          }} data-id="element-1779" data-mp="xi5tef84fwky" data-mpi={idx}>
                <p className="text-5xl md:text-6xl font-black mb-3" style={{
              color: NAVY,
              fontFamily: "'Playfair Display', Georgia, serif"
            }} data-id="element-1780" data-mp="ivgxsogzq3ju">
                  {stat.pct}
                </p>
                <p className="text-sm text-gray-700 leading-relaxed" data-id="element-1781" data-mp="rx7yfewobk6u">
                  {stat.desc}
                </p>
              </div>)}
          </div>

          {/* Quote banner */}
          <div className="rounded-2xl p-8 md:p-10 text-center relative" style={{
          backgroundColor: NAVY_DARK
        }} data-id="element-1782" data-mp="11tjfsjb4f085">
            <span className="absolute top-4 left-6 text-5xl opacity-40 font-serif leading-none" style={{
            color: GOLD
          }} data-id="element-1783" data-mp="wuq8x9qca7gy">
              «
            </span>
            <p className="text-lg md:text-xl text-white font-light italic leading-relaxed max-w-3xl mx-auto px-6" data-id="element-1784" data-mp="16m46hbyqcx7g">
              PROMAISON n'est pas né d'une idée théorique. Le programme est né
              de besoins exprimés par les employeurs eux-mêmes.
            </p>
            <span className="absolute bottom-4 right-6 text-5xl opacity-40 font-serif leading-none" style={{
            color: GOLD
          }} data-id="element-1785" data-mp="2myq3cx9pjmnd">
              »
            </span>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────── */}
      {/* CHIFFRES — 41 / 5 / 10 / 1 */}
      {/* ─────────────────────────────────────────────── */}
      <section className="py-20 md:py-24" style={{
      backgroundColor: NAVY
    }} data-id="element-1786" data-mp="23ahb6n85zgnv">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1787" data-mp="2890yos2ep0ic">
          <div className="text-center mb-14" data-id="element-1788" data-mp="289k9lotn2omv">
            <span className="text-sm font-semibold tracking-widest uppercase mb-4 block" style={{
            color: GOLD
          }} data-id="element-1789" data-mp="2d7vfjogtcezt">
              PROMAISON en chiffres
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight max-w-3xl mx-auto" style={{
            fontFamily: "'Playfair Display', Georgia, serif"
          }} data-id="element-1790" data-mp="1kjzp31hu7jbh">
              Depuis son lancement, PROMAISON accompagne concrètement les
              personnels de maison et leurs employeurs.
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6" data-id="element-1791" data-mp="1d0qbsx67n0h5">
            {[{
            num: '41',
            label: 'bénéficiaires déjà accompagnés'
          }, {
            num: '5',
            label: 'moniteurs formés par ADCC'
          }, {
            num: '10',
            label: 'jours de programme — théorie et pratique'
          }, {
            num: '1',
            label: "attestation de participation délivrée par ADCC à l'issue du parcours"
          }].map((stat, idx) => <div key={idx} className="rounded-xl p-6 md:p-8 text-center" style={{
            backgroundColor: NAVY_DARK
          }} data-id="element-1792" data-mp="kdcnfngkvd8n" data-mpi={idx}>
                <p className="text-6xl md:text-7xl font-black mb-3" style={{
              color: GOLD,
              fontFamily: "'Playfair Display', Georgia, serif"
            }} data-id="element-1793" data-mp="bb3g16glvzzh">
                  {stat.num}
                </p>
                <p className="text-blue-200 text-sm leading-relaxed" data-id="element-1794" data-mp="dqwsbuvu3vlp">
                  {stat.label}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────── */}
      {/* VIDÉO + AFFICHE */}
      {/* ─────────────────────────────────────────────── */}
      <section id="video" className="py-20 md:py-24 bg-gray-50 scroll-mt-24" data-id="element-1795" data-mp="3vz5o2za2vhjy">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1796" data-mp="3r02hhaeg939v">
          <div className="text-center mb-12" data-id="element-1797" data-mp="8to69j7kf7f6">
            <span className="text-sm font-semibold tracking-widest uppercase mb-4 block" style={{
            color: GOLD
          }} data-id="element-1798" data-mp="8qijiameshmm">
              En images
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight" style={{
            fontFamily: "'Playfair Display', Georgia, serif"
          }} data-id="element-1799" data-mp="1k677947hlkoh">
              Découvrez PROMAISON
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start" data-id="element-1800" data-mp="1sosn3s07g56f">
            {/* Video (larger) */}
            <div className="lg:col-span-3" data-id="element-1801" data-mp="1ds1kl9v8idcl">
              <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl aspect-video" style={{
              backgroundColor: NAVY
            }} data-id="element-1802" data-mp="1dt2x7irwf7wf">
                <iframe className="absolute inset-0 w-full h-full" src="https://www.youtube.com/embed/tAvL6v7B_po" title="Vidéo de présentation PROMAISON par ADCC" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen loading="lazy" data-id="element-1803" data-mp="26fno6i4gphzg"></iframe>
              </div>
              <div className="mt-5 text-center lg:text-left" data-id="element-1804" data-mp="21o5vehclenpk">
                <a href="https://youtu.be/tAvL6v7B_po" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold hover:underline" style={{
                color: NAVY
              }} data-id="element-1805" data-mp="2pejg1msulka4">
                  <PlayCircle className="w-5 h-5" style={{
                  color: GOLD
                }} data-id="element-1806" data-mp="340p7r0r5ypg7" />
                  Voir la vidéo sur YouTube
                  <ArrowRight className="w-4 h-4" data-id="element-1807" data-mp="2z0kmkxfb96jn" />
                </a>
              </div>
            </div>

            {/* Affiche PROMAISON */}
            <div className="lg:col-span-2" data-id="element-1808" data-mp="2q2s8fwlbs4xu">
              <p className="text-xs font-semibold tracking-widest uppercase mb-3 text-center lg:text-left" style={{
              color: GOLD
            }} data-id="element-1809" data-mp="2q1petgwmboe8">
                L'affiche officielle
              </p>
              <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-gray-200" data-id="element-1810" data-mp="2ga31nf0pzf8u">
                <img src="https://cdn.magicpatterns.com/uploads/4hxAWqSMUTmkMiaPwYmMZJ/PROMAISON_affiche_original_orthographe_fix.png" alt="Affiche PROMAISON par ADCC — Votre maison mérite mieux. Programme de renforcement de capacité des personnels de maison." className="w-full h-auto object-contain" loading="lazy" data-id="element-1811" data-mp="2bb040fn2kbaw" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────── */}
      {/* CONVICTION */}
      {/* ─────────────────────────────────────────────── */}
      <section className="py-20 md:py-28" style={{
      backgroundColor: GOLD_LIGHT
    }} data-id="element-1812" data-mp="msx77vxbpowz">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1813" data-mp="qvbuqtbxtcul">
          <span className="text-sm font-semibold tracking-widest uppercase mb-4 block" style={{
          color: GOLD
        }} data-id="element-1814" data-mp="3qafikdcb1e25">
            Notre conviction
          </span>
          <h2 className="text-3xl md:text-5xl font-bold leading-tight mb-8" style={{
          color: NAVY,
          fontFamily: "'Playfair Display', Georgia, serif"
        }} data-id="element-1815" data-mp="3v6w2swgdv89y">
            Compétence et emploi durable
          </h2>
          <div className="space-y-5" data-id="element-1816" data-mp="37kh85ql1b8vc">
            <p className="text-base md:text-lg leading-relaxed" style={{
            color: NAVY_DARK
          }} data-id="element-1817" data-mp="37jfmytab15zw">
              Chez ADCC, nous sommes convaincus que le renforcement des
              capacités constitue l'un des fondements de l'emploi durable.
            </p>
            <p className="text-base md:text-lg leading-relaxed" style={{
            color: NAVY_DARK
          }} data-id="element-1818" data-mp="2niw4yu1z3q95">
              En améliorant les pratiques des personnels de maison, PROMAISON
              contribue à renforcer la confiance entre employeurs et employés, à
              améliorer la qualité du service rendu aux familles et à favoriser
              des relations de travail plus stables dans la durée.
            </p>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────── */}
      {/* PROGRAMME — 4 thèmes */}
      {/* ─────────────────────────────────────────────── */}
      <section className="py-20 md:py-24 bg-white" data-id="element-1819" data-mp="3g4e8jvgfag10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1820" data-mp="1qsc4vx6gljms">
          <div className="mb-14 max-w-3xl" data-id="element-1821" data-mp="1lshc1o34ijz3">
            <span className="text-sm font-semibold tracking-widest uppercase mb-4 block" style={{
            color: GOLD
          }} data-id="element-1822" data-mp="1ltsdvid0dhnk">
              Le programme
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5 leading-tight" style={{
            fontFamily: "'Playfair Display', Georgia, serif"
          }} data-id="element-1823" data-mp="688cqimvox9x">
              Quatre thèmes fondamentaux, ancrés dans la réalité du terrain.
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-1824" data-mp="3epbg0qim3uib">
              PROMAISON combine apports théoriques, exercices pratiques et
              partage d'expériences, pour répondre aux situations concrètes
              rencontrées dans les foyers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1825" data-mp="39pyxuj2orbm2">
            {[{
            icon: Home,
            title: 'Entretien du domicile',
            items: ['Organisation du travail', "Techniques d'entretien", 'Utilisation du matériel']
          }, {
            icon: Droplets,
            title: 'Hygiène et sécurité',
            items: ['Utilisation des produits', 'Prévention des risques', "Bonnes pratiques d'hygiène"]
          }, {
            icon: Shirt,
            title: 'Gestion du linge',
            items: ['Tri et entretien des textiles', 'Techniques de repassage', 'Préservation des vêtements']
          }, {
            icon: HeartHandshake,
            title: 'Relation professionnelle',
            items: ["Communication avec l'employeur", 'Discrétion et respect', 'Ponctualité et fiabilité']
          }].map((mod, idx) => <Card key={idx} className="border border-gray-200 hover:shadow-lg transition-shadow" data-id="element-1826" data-mp="2bgmqsjdftwbt" data-mpi={idx}>
                <CardContent className="p-7" data-id="element-1827" data-mp="2fhorl4n06gfw">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-5" style={{
                backgroundColor: GOLD_LIGHT
              }} data-id="element-1828" data-mp="2figp4l05uo8t">
                    <mod.icon className="w-6 h-6" style={{
                  color: NAVY
                }} data-id="element-1829" data-mp="2pgntm4d9jdtx" />
                  </div>
                  <h3 className="font-bold text-lg mb-4" style={{
                color: NAVY
              }} data-id="element-1830" data-mp="2uefw1sbjaicd">
                    {mod.title}
                  </h3>
                  <ul className="space-y-2" data-id="element-1831" data-mp="3370z802omgg3">
                    {mod.items.map((item, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-600 leading-relaxed" data-id="element-1832" data-mp="339vl7w1cg9bm" data-mpi={i}>
                        <span style={{
                    color: GOLD
                  }} data-id="element-1833" data-mp="3d5p7i74v7h6l">
                          —
                        </span>
                        <span data-id="element-1834" data-mp="3i5l6tt2makft">{item}</span>
                      </li>)}
                  </ul>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────── */}
      {/* PARCOURS — Fondamental vs Expert */}
      {/* ─────────────────────────────────────────────── */}
      <section ref={parcoursRef} className="py-20 md:py-24 bg-gray-50" data-id="element-1835" data-mp="bc7j6qm1ahyq">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1836" data-mp="ba4t66kvsou0">
          <div className="mb-10 max-w-3xl" data-id="element-1837" data-mp="3jske4c6t3zp2">
            <span className="text-sm font-semibold tracking-widest uppercase mb-4 block" style={{
            color: GOLD
          }} data-id="element-1838" data-mp="3fq5zy5e31o44">
              Deux parcours
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-4" style={{
            fontFamily: "'Playfair Display', Georgia, serif"
          }} data-id="element-1839" data-mp="3tjnibmv18usk">
              Un programme adapté à chaque situation.
            </h2>
            <p className="text-gray-600" data-id="element-1840" data-mp="2w2yhnwhtjykr">
              Choisissez votre profil pour découvrir le parcours qui vous
              correspond.
            </p>
          </div>

          {/* Audience filter */}
          <div className="flex flex-wrap gap-3 mb-10" data-id="element-1841" data-mp="1gbw9h4day1yc">
            <button type="button" onClick={() => setAudience('personnel')} className={`flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold transition-all ${audience === 'personnel' ? 'shadow-md text-white' : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-400'}`} style={audience === 'personnel' ? {
            backgroundColor: NAVY
          } : {}} data-id="element-1842" data-mp="12jg2xtl6zlso">
              <GraduationCap className="w-4 h-4" data-id="element-1843" data-mp="1lagzkayub592" />
              Personnel de maison
            </button>
            <button type="button" onClick={() => setAudience('employeur')} className={`flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold transition-all ${audience === 'employeur' ? 'shadow-md text-white' : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-400'}`} style={audience === 'employeur' ? {
            backgroundColor: NAVY
          } : {}} data-id="element-1844" data-mp="1q8isdnm2iixq">
              <Briefcase className="w-4 h-4" data-id="element-1845" data-mp="u1axmswqro6y" />
              Employeur
            </button>
            {audience !== null && <button type="button" onClick={() => setAudience(null)} className="flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors underline" data-id="element-1846" data-mp="g1t5c3k8lssu">
                Voir les deux parcours
              </button>}
          </div>

          <div className={`grid grid-cols-1 gap-8 ${audience === null ? 'lg:grid-cols-2' : 'max-w-2xl mx-auto'}`} data-id="element-1847" data-mp="b49piz7w57ys">
            {/* Fondamental — Personnel de maison */}
            {(audience === null || audience === 'personnel') && <div id="parcours-personnel" className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow" data-id="element-1848" data-mp="1dy7k8rdgy8w">
                {/* Blue header */}
                <div className="p-8 md:p-10" style={{
              backgroundColor: '#3B6BA0'
            }} data-id="element-1849" data-mp="3ohcuorhser2m">
                  <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase mb-5 bg-white/20 text-white" data-id="element-1850" data-mp="34x4fdlb6vt26">
                    Personnel de maison
                  </span>
                  <h3 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{
                fontFamily: "'Playfair Display', Georgia, serif"
              }} data-id="element-1851" data-mp="3dv6c3onw3zjn">
                    Parcours Fondamental
                  </h3>
                  <p className="text-blue-100 leading-relaxed" data-id="element-1852" data-mp="3iwnfbqtujy3e">
                    Pour les agents d'entretien et assistants(es) ménagers(ères)
                    souhaitant renforcer leurs capacités et améliorer leurs
                    pratiques.
                  </p>
                </div>

                {/* White content */}
                <div className="p-8 md:p-10" data-id="element-1853" data-mp="ahc2gtoic34a">
                  <div className="flex items-center gap-2 px-4 py-3 rounded-lg mb-6 text-sm" style={{
                backgroundColor: '#EEF4FB',
                color: NAVY
              }} data-id="element-1854" data-mp="5hhasnwwr9i6">
                    <Calendar className="w-4 h-4 flex-shrink-0" data-id="element-1855" data-mp="2z7md3l831lsi" />
                    <span className="font-medium" data-id="element-1856" data-mp="3491xdb2fro9u">
                      10 jours — théorie et pratique en situation réelle
                    </span>
                  </div>

                  <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{
                color: NAVY_DARK
              }} data-id="element-1857" data-mp="3d13tix5h2ieb">
                    Thèmes abordés
                  </p>
                  <div className="flex flex-wrap gap-2 mb-6" data-id="element-1858" data-mp="3mxysti9kkmtn">
                    {['Entretien du domicile', 'Hygiène et sécurité', 'Gestion du linge', 'Relation professionnelle'].map(theme => <span key={theme} className="px-3 py-1.5 rounded-full text-xs font-medium" style={{
                  backgroundColor: '#EEF4FB',
                  color: NAVY
                }} data-id="element-1859" data-mp="3vxdhqda38pc1">
                        {theme}
                      </span>)}
                  </div>

                  <div className="rounded-lg p-4 mb-7 border-l-4" style={{
                backgroundColor: '#EEF4FB',
                borderColor: NAVY
              }} data-id="element-1860" data-mp="1u40l7zqa5swr">
                    <p className="text-sm flex items-start gap-2" style={{
                  color: NAVY
                }} data-id="element-1861" data-mp="1kcw52wovc20c">
                      <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-1862" data-mp="1g8ogr0mboo2n" />
                      <span data-id="element-1863" data-mp="2d0ia89dqvepb">
                        Attestation de participation PROMAISON délivrée par ADCC
                        à l'issue du parcours
                      </span>
                    </p>
                  </div>

                  <div className="pt-6 border-t border-gray-200 text-center" data-id="element-1864" data-mp="2426mqg86cx13">
                    <p className="text-xs tracking-wide mb-2" style={{
                  color: GOLD
                }} data-id="element-1865" data-mp="1z0zfci9y1zuy">
                      Participation aux frais d'encadrement pédagogique
                    </p>
                    <p className="text-4xl md:text-5xl font-bold" style={{
                  color: NAVY,
                  fontFamily: "'Playfair Display', Georgia, serif"
                }} data-id="element-1866" data-mp="nogfx1po7unr">
                      40 000 FCFA
                    </p>
                  </div>
                </div>
              </div>}

            {/* Expert — Employeur */}
            {(audience === null || audience === 'employeur') && <div id="parcours-employeur" className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow" data-id="element-1867" data-mp="26hcfkj4fu79x">
                {/* Dark navy header */}
                <div className="p-8 md:p-10" style={{
              backgroundColor: NAVY_DARK
            }} data-id="element-1868" data-mp="2z7l18ky6n9t9">
                  <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase mb-5 bg-white/20 text-white" data-id="element-1869" data-mp="2lapddmd9vdt5">
                    Employeur
                  </span>
                  <h3 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{
                fontFamily: "'Playfair Display', Georgia, serif"
              }} data-id="element-1870" data-mp="19vslm4ppeai3">
                    Parcours Expert
                  </h3>
                  <p className="text-blue-100 leading-relaxed" data-id="element-1871" data-mp="12age145w5cd8">
                    Pour les employeurs souhaitant accompagner directement le
                    renforcement des capacités de leur personnel de maison.
                  </p>
                </div>

                {/* White content */}
                <div className="p-8 md:p-10" data-id="element-1872" data-mp="nkhil95q0f1g">
                  <p className="text-xs font-semibold tracking-widest uppercase mb-4" style={{
                color: NAVY_DARK
              }} data-id="element-1873" data-mp="nlaf7hayto5v">
                    Le parcours comprend
                  </p>
                  <ol className="space-y-3 mb-7" data-id="element-1874" data-mp="xcx1q9sr79nx">
                    {['Diagnostic initial à votre domicile', 'Séances de renforcement des capacités individualisées', 'Mise en situation pratique', 'Évaluation finale', "Rapport de progression remis à l'employeur"].map((step, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-1875" data-mp="1zwms2yarkxxm" data-mpi={idx}>
                        <span className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs border-2" style={{
                    borderColor: NAVY,
                    color: NAVY
                  }} data-id="element-1876" data-mp="24w92z134e239">
                          {idx + 1}
                        </span>
                        <span className="text-sm text-gray-700 leading-relaxed pt-0.5" data-id="element-1877" data-mp="1gzd2t0ircq8g">
                          {step}
                        </span>
                      </li>)}
                  </ol>

                  <div className="rounded-lg p-4 mb-7 border-l-4" style={{
                backgroundColor: '#EEF4FB',
                borderColor: NAVY
              }} data-id="element-1878" data-mp="1pz05dpn84233">
                    <p className="text-sm flex items-start gap-2" style={{
                  color: NAVY
                }} data-id="element-1879" data-mp="31eovuqzorj8n">
                      <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-1880" data-mp="36d8hsrea4v10" />
                      <span data-id="element-1881" data-mp="3ghowvijxq83r">
                        Attestation de participation PROMAISON délivrée par ADCC
                        au personnel accompagné
                      </span>
                    </p>
                  </div>

                  <div className="pt-6 border-t border-gray-200 text-center" data-id="element-1882" data-mp="36r3pb5ma9t8p">
                    <p className="text-xs tracking-wide mb-2" style={{
                  color: GOLD
                }} data-id="element-1883" data-mp="32qjqlxm2ahkg">
                      Participation aux frais d'encadrement pédagogique
                    </p>
                    <p className="text-4xl md:text-5xl font-bold mb-1" style={{
                  color: NAVY,
                  fontFamily: "'Playfair Display', Georgia, serif"
                }} data-id="element-1884" data-mp="2srulz6j6etm5">
                      100 000 FCFA
                    </p>
                    <p className="text-sm italic" style={{
                  color: GOLD
                }} data-id="element-1885" data-mp="2jtimsg48uni3">
                      Pris en charge par l'employeur
                    </p>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────── */}
      {/* SESSION — date, contact CTAs */}
      {/* ─────────────────────────────────────────────── */}
      <section id="session" className="py-20 md:py-24 relative overflow-hidden" style={{
      backgroundColor: NAVY
    }} data-id="element-1886" data-mp="rjswjs745466">
        <div className="absolute inset-0 opacity-10" data-id="element-1887" data-mp="wg9n0t1sbumf">
          <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(45deg, #D4A574 25%, transparent 25%, transparent 75%, #D4A574 75%)',
          backgroundSize: '40px 40px'
        }} data-id="element-1888" data-mp="hkk6vbja6bpm" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1889" data-mp="8lq3n2bg2n33">
          <span className="text-sm font-semibold tracking-widest uppercase mb-4 block" style={{
          color: GOLD
        }} data-id="element-1890" data-mp="dk1e9f9kd1md">
            Rejoindre PROMAISON
          </span>

          <h2 className="text-4xl md:text-5xl font-bold text-white mb-8" style={{
          fontFamily: "'Playfair Display', Georgia, serif"
        }} data-id="element-1891" data-mp="27xj0myy8fsft">
            Prochaine session
          </h2>

          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full mb-6" style={{
          backgroundColor: NAVY_DARK
        }} data-id="element-1892" data-mp="2d363s2491mki">
            <Calendar className="w-5 h-5" style={{
            color: GOLD
          }} data-id="element-1893" data-mp="2qvcjjn3hprjf" />
            <span className="text-lg font-semibold" style={{
            color: GOLD
          }} data-id="element-1894" data-mp="2m0orxfd6wh0m">
              Octobre 2026
            </span>
          </div>

          <p className="text-blue-100 mb-12 text-base md:text-lg max-w-2xl mx-auto leading-relaxed" data-id="element-1895" data-mp="2vrjfcn269mnn">
            Le nombre de participants est volontairement limité afin de garantir
            un accompagnement de qualité.{' '}
            <span className="font-bold text-white" data-id="element-1896" data-mp="35rk1egrfegvj">— Places limitées.</span>
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10 max-w-3xl mx-auto" data-id="element-1897" data-mp="1feuwgdls919g">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 text-left" data-id="element-1898" data-mp="1be2lnfx9iob2">
              <p className="font-bold mb-3 text-base" style={{
              color: GOLD
            }} data-id="element-1899" data-mp="kaeycos09yl3">
                Vous êtes personnel de maison ?
              </p>
              <p className="text-blue-100 text-sm leading-relaxed" data-id="element-1900" data-mp="akd8brg3e63j">
                Contactez-nous pour connaître les modalités de participation au
                Parcours Fondamental.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 text-left" data-id="element-1901" data-mp="1l8bcpk6ikth">
              <p className="font-bold mb-3 text-base" style={{
              color: GOLD
            }} data-id="element-1902" data-mp="3sp6q5wceep1y">
                Vous êtes employeur ?
              </p>
              <p className="text-blue-100 text-sm leading-relaxed" data-id="element-1903" data-mp="3istzz8g2atby">
                Contactez-nous pour inscrire votre personnel au Parcours Expert.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center" data-id="element-1904" data-mp="1h0p9m1e7o43c">
            <a href="tel:+22901901175555" data-id="element-1905" data-mp="1pihh5jsbnr11">
              <Button size="lg" className="text-base px-6 py-5 gap-2 shadow-xl bg-white" style={{
              color: NAVY
            }} data-id="element-1906" data-mp="1phg6hgf7x6ju">
                <Phone className="w-4 h-4" style={{
                color: '#E91E63'
              }} data-id="element-1907" data-mp="1thghss70t1mo" />
                +229 01 90 11 75 55
              </Button>
            </a>
            <a href="mailto:contact@adcc.africa?subject=Inscription PROMAISON - Session Octobre 2026" data-id="element-1908" data-mp="2394g9d1zvixn">
              <Button size="lg" className="text-base px-6 py-5 gap-2 shadow-xl bg-white" style={{
              color: NAVY
            }} data-id="element-1909" data-mp="16ky6ycu4o2d1">
                <Mail className="w-4 h-4" style={{
                color: '#3B6BA0'
              }} data-id="element-1910" data-mp="1fk1o3b9qi7ow" />
                contact@adcc.africa
              </Button>
            </a>
          </div>

          <p className="mt-8 text-sm text-blue-200" data-id="element-1911" data-mp="1kh1whsu23mj6">www.adcc.africa</p>
        </div>

        {/* Inner footer ribbon */}
        <div className="relative mt-20 -mb-20 md:-mb-24 py-6 border-t" style={{
        backgroundColor: NAVY_DARK,
        borderColor: 'rgba(255,255,255,0.1)'
      }} data-id="element-1912" data-mp="2vybcg5dz7wfq">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1913" data-mp="26bz72x5ntiep">
            <p className="text-xs md:text-sm text-blue-200" data-id="element-1914" data-mp="21atd6w09jp3l">
              <span className="font-bold text-white" data-id="element-1915" data-mp="2g8lsbn1webh9">PROMAISON par ADCC</span> —
              Association Dignité Compétence Création ·{' '}
              <span className="italic" data-id="element-1916" data-mp="2ba0m96zysxkw">La dignité par la compétence.</span> · ©
              2026 ADCC — Tous droits réservés
            </p>
          </div>
        </div>
      </section>
    </Layout>;
}